import React, { useState, useEffect, useCallback } from 'react';
import { fetchImpactSurveys, deleteImpactSurvey, exportImpactSurveyCsv } from '../../api/client';
import formatDate from '../../utils/formatDate';
import './ImpactSurveyList.css';

const ROLE_OPTIONS = ['Attendee', 'Volunteer', 'Leader'];
const SURVEY_TYPE_OPTIONS = [
  { value: 'attendee', label: 'Attendee' },
  { value: 'volunteer_leader', label: 'Volunteer / Leader' },
];

function surveyTypeLabel(value) {
  const found = SURVEY_TYPE_OPTIONS.find((o) => o.value === value);
  return found ? found.label : (value || '\u2014');
}

function getRatingBadge(value, max = 5) {
  if (value == null) return '';
  const ratio = value / max;
  if (ratio >= 0.8) return 'badge-positive';
  if (ratio >= 0.5) return 'badge-pending';
  return 'badge-negative';
}

function getOutcomeBadge(outcome) {
  if (outcome === 'completed') return 'badge-positive';
  if (outcome === 'partially_completed') return 'badge-pending';
  return 'badge-negative';
}

function ResponseValue({ question, response }) {
  if (!response) return null;
  if (question.type === 'rating') {
    return (
      <span className={`badge ${getRatingBadge(response.value, question.max)}`}>
        {response.value} / {question.max}
      </span>
    );
  }
  if (question.type === 'multi_select') {
    return (
      <div className="impact-survey-chips">
        {(response.value || []).map((v) => (
          <span key={v} className="impact-survey-chip">{v}</span>
        ))}
      </div>
    );
  }
  if (question.type === 'single_choice') {
    return <div className="impact-survey-choice">{response.value}</div>;
  }
  return <div className="impact-survey-text-response">{response.value || 'No response'}</div>;
}

export default function ImpactSurveyList({
  questions = [],
  surveyTypeFilter = '',
  onSurveyTypeChange,
}) {
  const [surveys, setSurveys] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [skip, setSkip] = useState(0);
  const [roleFilter, setRoleFilter] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const limit = 20;

  const loadSurveys = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit, skip };
      if (roleFilter) params.role = roleFilter;
      if (surveyTypeFilter) params.survey_type = surveyTypeFilter;
      const res = await fetchImpactSurveys(params);
      setSurveys(res.data || []);
      setTotal(res.meta?.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [skip, roleFilter, surveyTypeFilter]);

  useEffect(() => {
    loadSurveys();
  }, [loadSurveys]);

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (confirmId === id) {
      try {
        await deleteImpactSurvey(id);
        setConfirmId(null);
        loadSurveys();
      } catch (err) {
        setError(err.message);
      }
    } else {
      setConfirmId(id);
    }
  };

  const handleCancelDelete = (e) => {
    e.stopPropagation();
    setConfirmId(null);
  };

  const handleRoleChange = (e) => {
    setRoleFilter(e.target.value);
    setSkip(0);
  };

  const handleSurveyTypeChange = (e) => {
    setSkip(0);
    if (onSurveyTypeChange) onSurveyTypeChange(e.target.value);
  };

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await exportImpactSurveyCsv({
        survey_type: surveyTypeFilter,
        role: roleFilter,
      });
    } catch (err) {
      setExportError(err.message);
    } finally {
      setExporting(false);
    }
  };

  if (loading && surveys.length === 0) {
    return <div className="loading">Loading IMPACT surveys...</div>;
  }

  if (error) {
    return <div className="error-message">Failed to load IMPACT surveys: {error}</div>;
  }

  const hasMore = skip + limit < total;
  const hasPrev = skip > 0;

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <h3 style={{ margin: 0 }}>IMPACT 2025 Survey Responses</h3>
        <button className="btn-secondary" onClick={handleExport} disabled={exporting}>
          {exporting ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>
      {exportError && (
        <div className="error-message" style={{ marginBottom: '0.5rem' }}>
          Export failed: {exportError}
        </div>
      )}
      <div className="impact-survey-role-filter">
        <label htmlFor="impact-survey-type-filter">Filter by survey:</label>
        <select
          id="impact-survey-type-filter"
          value={surveyTypeFilter}
          onChange={handleSurveyTypeChange}
        >
          <option value="">All</option>
          {SURVEY_TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <label htmlFor="impact-role-filter" style={{ marginLeft: '0.75rem' }}>Filter by role:</label>
        <select id="impact-role-filter" value={roleFilter} onChange={handleRoleChange}>
          <option value="">All</option>
          {ROLE_OPTIONS.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      </div>

      {surveys.length === 0 ? (
        <p>No IMPACT survey responses{roleFilter ? ` for role "${roleFilter}"` : ''} yet.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Phone</th>
              <th>Survey</th>
              <th>Role</th>
              <th>Age</th>
              <th>Overall</th>
              <th>2026 Likely</th>
              <th>Outcome</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {surveys.map((s) => (
              <React.Fragment key={s._id}>
                <tr onClick={() => toggleExpand(s._id)} style={{ cursor: 'pointer' }}>
                  <td>{formatDate(s.created_at)}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    {s.phone_number || 'N/A'}
                  </td>
                  <td>
                    <span className="badge badge-neutral">{surveyTypeLabel(s.survey_type)}</span>
                  </td>
                  <td>{s.role || '\u2014'}</td>
                  <td>{s.age_group || '\u2014'}</td>
                  <td>
                    {s.overall_experience != null ? (
                      <span className={`badge ${getRatingBadge(s.overall_experience, 5)}`}>
                        {s.overall_experience} / 5
                      </span>
                    ) : '\u2014'}
                  </td>
                  <td>
                    {s.likelihood_to_attend_2026 != null ? (
                      <span className={`badge ${getRatingBadge(s.likelihood_to_attend_2026, 5)}`}>
                        {s.likelihood_to_attend_2026} / 5
                      </span>
                    ) : '\u2014'}
                  </td>
                  <td>
                    <span className={`badge ${getOutcomeBadge(s.call_outcome)}`}>
                      {(s.call_outcome || 'unknown').replace('_', ' ')}
                    </span>
                  </td>
                  <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                    {confirmId === s._id ? (
                      <>
                        <button className="btn-danger-sm" onClick={(e) => handleDelete(e, s._id)}>Confirm</button>
                        {' '}
                        <button className="btn-secondary-sm" onClick={handleCancelDelete}>Cancel</button>
                      </>
                    ) : (
                      <button className="btn-icon" onClick={(e) => handleDelete(e, s._id)} title="Delete">&#128465;</button>
                    )}
                  </td>
                </tr>
                {expandedId === s._id && (
                  <tr className="impact-survey-detail-row">
                    <td colSpan={9}>
                      <div className="impact-survey-details">
                        <div className="impact-survey-meta">
                          <span className="detail-label">Call ID:</span>{' '}
                          <span className="detail-value">{s.call_id || 'N/A'}</span>
                        </div>
                        <div className="impact-survey-responses-grid">
                          {questions.map((q) => {
                            const resp = (s.responses || {})[q.key];
                            if (!resp) return null;
                            return (
                              <div key={q.key} className="impact-survey-response-item">
                                <div className="impact-survey-response-header">
                                  <span className="impact-survey-question-label">{q.label}</span>
                                </div>
                                <ResponseValue question={q} response={resp} />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}

      {(hasPrev || hasMore) && (
        <div className="submission-pagination">
          <button
            className="btn-secondary"
            disabled={!hasPrev}
            onClick={() => setSkip((prev) => Math.max(0, prev - limit))}
          >
            Previous
          </button>
          <span className="pagination-info">
            {total === 0 ? 0 : skip + 1}–{Math.min(skip + limit, total)} of {total}
          </span>
          <button
            className="btn-secondary"
            disabled={!hasMore}
            onClick={() => setSkip((prev) => prev + limit)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
